import type { Job } from '../../domain/entities/job.ts';
import type {
  ScheduleItem,
  ScheduleShiftRecord,
  ShiftReason,
} from '../../domain/entities/schedule.ts';
import type { ScheduleShift } from './dependencies.ts';

/**
 * How many movements an item keeps.
 *
 * A window, and — by the maintainer's decision of 2026-10-12 — the only
 * record there will be. A job audit log was considered and declined: it
 * cannot share a batch with the schedule change it describes (a cascade
 * down a 500-item chain already produces exactly 500 writes against a cap
 * of 500), so it would have meant either a server-side trigger that no
 * in-memory adapter can reproduce, or a second batch that can silently miss
 * entries. Two hundred replans of a *single item* is a schedule with worse
 * problems than a short log. See `docs/features/02-schedule.md` §4.1.
 *
 * Because this is the whole record, the boundary is reported rather than
 * hidden: `historyIsCapped` lets a view say "the most recent 200" instead
 * of implying it has them all.
 */
export const MAX_SHIFT_HISTORY = 200;

export interface ShiftAttribution {
  readonly byUid: string;
  readonly at: string;
  /** True when this movement was forced by something else moving. */
  readonly cascade: boolean;
  readonly reason?: ShiftReason;
  readonly note?: string;
}

/** Turn a computed movement into the record kept on the item. */
export function toShiftRecord(
  shift: ScheduleShift,
  attribution: ShiftAttribution,
): ScheduleShiftRecord {
  return {
    at: attribution.at,
    byUid: attribution.byUid,
    fromStart: shift.fromStart,
    toStart: shift.toStart,
    fromEnd: shift.fromEnd,
    toEnd: shift.toEnd,
    reason: attribution.reason,
    note: attribution.note,
    cascade: attribution.cascade,
  };
}

/**
 * Prepend a movement, keeping the window most-recent-first.
 *
 * The oldest falls off the end. Most-recent-first is what makes that the
 * right end to lose: "why did this move last week" is asked far more often
 * than "why did it move in January".
 */
export function withShift(item: ScheduleItem, record: ScheduleShiftRecord): ScheduleShiftRecord[] {
  return [record, ...item.shifts].slice(0, MAX_SHIFT_HISTORY);
}

/**
 * Whether this item's history has started losing its oldest entries.
 *
 * Reported so a view can say "the most recent 200" rather than presenting a
 * window as a complete record. It cannot say how many were lost — nothing
 * counts them, which is the deliberate cost of having no audit log — so the
 * honest statement is that the list is capped, not how far over it ran.
 *
 * `>=` rather than `===` because an item written before the cap existed, or
 * by a future version with a larger one, should still read as capped rather
 * than as complete.
 */
export function historyIsCapped(item: Pick<ScheduleItem, 'shifts'>): boolean {
  return item.shifts.length >= MAX_SHIFT_HISTORY;
}

/**
 * Whether moving this item needs a reason.
 *
 * Required once a date has left the building: the schedule is online *and*
 * this item is visible to someone outside the company, whether that is a sub
 * assigned to it or the owner. Before that the dates are a draft and
 * demanding a reason for every adjustment would only train people to pick
 * the first option in the list.
 */
export function requiresReason(
  item: Pick<ScheduleItem, 'visibility' | 'assignees'>,
  job: Pick<Job, 'scheduleOnline'>,
): boolean {
  if (!job.scheduleOnline) return false;
  const hasSub =
    item.visibility.subs && item.assignees.some((assignee) => assignee.kind === 'contact');
  return hasSub || item.visibility.owner;
}

/** The most recent movement, or null for an item that has never moved. */
export function latestShift(item: ScheduleItem): ScheduleShiftRecord | null {
  return item.shifts[0] ?? null;
}

/**
 * Movements somebody chose, as opposed to ones forced by a dependency.
 *
 * The baseline tab reports this rather than the total: an item pushed five
 * times by its predecessor has not been replanned five times, and showing it
 * as though it had points at the wrong work.
 */
export function directShiftCount(item: ScheduleItem): number {
  return item.shifts.filter((shift) => !shift.cascade).length;
}
